import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { RealhubArtworkTemplateModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/realhubArtworkTemplate/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/realhubArtworkTemplate/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/realhubArtworkTemplate/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/realhubArtworkTemplate/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/realhubArtworkTemplate/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/realhubArtworkTemplate/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/realhubArtworkTemplate/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/realhubArtworkTemplate/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/realhubArtworkTemplate/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/realhubArtworkTemplate/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/realhubArtworkTemplate/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/realhubArtworkTemplate/DELETE_FAILURE'

export type RealhubArtworkTemplateModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: RealhubArtworkTemplateModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadRealhubArtworkTemplates(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().realhubArtworkTemplates.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/realbase/artwork-templates', options)
      .then(({ data }: { data: RealhubArtworkTemplateModel[] }) => {
        const normalizedJson = normalize(data.data, Schemas.REALHUB_ARTWORK_TEMPLATE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadRealhubArtworkTemplate(realhubArtworkTemplateId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.realhubArtworkTemplates.loadedIds]
    const loadedForKeys = [...state.realhubArtworkTemplates.loadedForKeys]

    if (realhubArtworkTemplateId && !loadedIds.includes(realhubArtworkTemplateId)) {
      loadedIds.push(realhubArtworkTemplateId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)) {
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/realbase/artwork-templates/${realhubArtworkTemplateId}`, options)
      .then(({ data }: { data: RealhubArtworkTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.REALHUB_ARTWORK_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createRealhubArtworkTemplate(realhubArtworkTemplate: Partial<RealhubArtworkTemplateModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...realhubArtworkTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/realbase/artwork-templates', options, config)
      .then(({ data }: { data: RealhubArtworkTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.REALHUB_ARTWORK_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateRealhubArtworkTemplate(
  realhubArtworkTemplate: UpdateParams<RealhubArtworkTemplateModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      ...realhubArtworkTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/realbase/artwork-templates/${realhubArtworkTemplate.id}`, options, config)
      .then(({ data }: { data: RealhubArtworkTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.REALHUB_ARTWORK_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteRealhubArtworkTemplate(realhubArtworkTemplate: DeleteParams<RealhubArtworkTemplateModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/realbase/artwork-templates/${realhubArtworkTemplate.id}`, {}, config)
      .then(({ status }) => {
        if (status !== 200) throw new Error()

        const deletedEntity = { id: realhubArtworkTemplate.id }
        const normalizedJson = normalize(deletedEntity, Schemas.REALHUB_ARTWORK_TEMPLATE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data: deletedEntity }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: RealhubArtworkTemplateModuleState = initialState,
  action: Action = { type: '' },
): RealhubArtworkTemplateModuleState {
  switch (action.type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
